import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../ConfirmModal';
import RecordFormModal from './RecordFormModal';
import RecordDetailsModal from './RecordDetailsModal';
import { apiErrorMessage } from '../../../utils/apiError';

/**
 * Add / edit / details / delete as dialogs, for any list page.
 *
 * Give it the module's API object and a field schema; it returns the openers
 * plus a `modals` element to drop into the page. Nothing navigates away.
 *
 *   const actions = useRecordActions({
 *     api, fields, labels: { create, edit, details, deleteConfirm },
 *     toPayload, toDetails, onChanged: load,
 *   });
 *
 * `toPayload(values)`  — shape the form values for the API (defaults to as-is)
 * `toDetails(record)`  — [{ label, value }] for the details dialog
 */
const useRecordActions = ({
  api,
  fields,
  labels = {},
  toPayload = (v) => v,
  toDetails = () => [],
  onChanged,
  onError,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [details, setDetails] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const openCreate = useCallback(() => {
    setForm({
      title: labels.create || t('tree.add'),
      initial: null,
      submit: (values) => api.create(toPayload(values)),
    });
  }, [api, labels.create, t, toPayload]);

  const openEdit = useCallback(
    (record) => {
      setForm({
        title: labels.edit || t('tree.edit'),
        initial: record,
        submit: (values) => api.update(record.id, toPayload(values, record)),
      });
    },
    [api, labels.edit, t, toPayload]
  );

  const openDetails = useCallback(
    (record) => {
      setDetails({
        title: labels.details || t('tree.details'),
        items: toDetails(record),
      });
    },
    [labels.details, t, toDetails]
  );

  const openDelete = useCallback((record) => setPendingDelete(record), []);

  const submitForm = async (values) => {
    await form.submit(values);
    setForm(null);
    if (onChanged) onChanged();
  };

  const runDelete = async () => {
    const record = pendingDelete;
    setPendingDelete(null);
    try {
      await api.delete(record.id);
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Delete failed:', err);
      if (onError) onError(apiErrorMessage(err));
    }
  };

  const modals = (
    <>
      <RecordFormModal
        show={Boolean(form)}
        title={form?.title}
        fields={fields}
        initial={form?.initial}
        onSubmit={submitForm}
        onClose={() => setForm(null)}
      />
      <RecordDetailsModal
        show={Boolean(details)}
        title={details?.title}
        items={details?.items || []}
        onClose={() => setDetails(null)}
      />
      <ConfirmModal
        show={Boolean(pendingDelete)}
        title={t('common.confirm')}
        message={labels.deleteConfirm || t('tree.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={runDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );

  return { openCreate, openEdit, openDetails, openDelete, modals };
};

export default useRecordActions;
